'use client';

import { ReactNode, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTestMode } from '@/hooks/useTestMode';
import styles from './index.module.scss';

let consentState = false;

window.addEventListener('tcStringChangedOrAvailable', (event: Event) => {
  const { detail: { tcData } } = event as CustomEvent<{ tcData?: { purpose?: { consents?: boolean[] } } }>;

  if (tcData && tcData.purpose && tcData.purpose.consents) {
    const { consents } = tcData.purpose;
    // Mapowanie zgód OneTrust na TCF:
    // C0002 (Analytics) → Purpose 7 (Measurement)
    // C0003 (Functional) → Purpose 1 (Store/access information on a device)
    // C0004 (Targeting) → Purpose 2 (Use limited data to select advertising)
    consentState = consents[1] === true && consents[2] === true && consents[7] === true;
  } else {
    consentState = false;
  }

  window.dispatchEvent(new CustomEvent('rtc-widget:embed-consent-state-changed', { detail: { consentState } }));
});

export default function EmbedContainer ({
  children,
  isAutoExpand = false,
  isNoConsentNeeded = false,
}: {
  children: ReactNode;
  isAutoExpand?: boolean;
  isNoConsentNeeded?: boolean;
}) {
  const { inView, ref: embedContainerRef } = useInView({ rootMargin: '500px 0px', threshold: 0, triggerOnce: true });
  const { isTestMode } = useTestMode();
  const [isConsentGiven, setIsConsentGiven] = useState(isTestMode || consentState);
  const [isContentExpanded, setIsContentExpanded] = useState(isAutoExpand);

  useEffect(() => {
    const handleConsentStateChanged = (event: Event) => {
      const { detail: { consentState } } = event as CustomEvent<{ consentState: boolean }>;
      setIsConsentGiven(consentState);
    };

    window.addEventListener('rtc-widget:embed-consent-state-changed', handleConsentStateChanged);
    return () => {
      window.removeEventListener('rtc-widget:embed-consent-state-changed', handleConsentStateChanged);
    };
  }, []);

  const handleButtonOpenConsentScreenClick = () => {
    window.OneTrust?.ToggleInfoDisplay?.();
  };

  const handleExpandContentClick = () => {
    setIsContentExpanded(true);
  };

  if (isNoConsentNeeded || isConsentGiven) {
    const ExpandEmbedScreen = (
      <div className={styles['embed-container-expand-content']}>
        <button
          className={styles['embed-container-expand-content__button']}
          onClick={handleExpandContentClick}
          type='button'
        >
          Rozwiń
          <svg height='5' width='8' xmlns='http://www.w3.org/2000/svg'>
            <path d='M7 0 4 3 1 0 0 1l4 4 4-4z' fill='currentcolor' />
          </svg>
        </button>
      </div>
    );

    return (
      <div
        className={styles['embed-container']}
        data-shall-expand={isContentExpanded}
        ref={embedContainerRef}
      >
        {inView ? <>{children}</> : null}
        {!isContentExpanded ? ExpandEmbedScreen : null}
      </div>
    );
  } else {
    return (
      <div className={styles['embed-container-cookie-screen']}>
        <span className={styles['embed-container-cookie-screen__icon']} />
        <span className={styles['embed-container-cookie-screen__text']}>
          Aby wyświetlić post, musisz udzielić zgody na używanie plików cookie.
        </span>
        <button
          className={styles['embed-container-cookie-screen__button']}
          onClick={handleButtonOpenConsentScreenClick}
          type='button'
        >
          Udziel zgody
        </button>
      </div>
    );
  }
}
